import React from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import { formatUTCToChicago } from '../../../utils/datetime/datetime-comprehensive';

// Per-assignment status/label/date pure calculation helpers for
// useAssignments. Extracted VERBATIM, parameterized on `resubmissionAllowed`
// instead of closing over it.

export const getStatus = (assignment, resubmissionAllowed) => {
  // Weekly week-specific status should be derived from submitted_week_numbers,
  // not the base row's submitted_at/status (which may point to a different week).
  if (assignment.allow_weekly_submissions && assignment.week_number) {
    let submittedWeeks = assignment.submitted_week_numbers || [];
    let draftWeeks = assignment.draft_week_numbers || [];
    if (typeof submittedWeeks === 'string') {
      try {
        submittedWeeks = JSON.parse(submittedWeeks);
      } catch (e) {
        submittedWeeks = [];
      }
    }
    if (typeof draftWeeks === 'string') {
      try {
        draftWeeks = JSON.parse(draftWeeks);
      } catch (e) {
        draftWeeks = [];
      }
    }

    const isSubmittedForThisWeek = Array.isArray(submittedWeeks)
      ? submittedWeeks.some(w => Number(w) === Number(assignment.week_number))
      : false;
    const isDraftForThisWeek = Array.isArray(draftWeeks)
      ? draftWeeks.some(w => Number(w) === Number(assignment.week_number))
      : false;

    if (isSubmittedForThisWeek) {
      return 'submitted';
    }

    if (isDraftForThisWeek) {
      return 'draft';
    }
  }

  // Use the status from the backend API if available
  if (assignment.status) {
    return assignment.status;
  }

  // Fallback logic if backend status is not available
  // Check if assignment has been graded (has a grade value)
  if (assignment.grade !== null && assignment.grade !== undefined) {
    return 'graded';
  }

  // Check if assignment has been submitted (has submitted_at timestamp)
  if (assignment.submitted_at !== null && assignment.submitted_at !== undefined) {
    const dueDate = new Date(assignment.due_date);
    const submittedDate = new Date(assignment.submitted_at);
    return submittedDate > dueDate ? 'late' : 'submitted';
  }

  // If no submitted_at but resubmission is allowed, this means the assignment was previously submitted
  // but is now a draft for resubmission. We should still show it as 'submitted' in the list
  // because the student has already submitted it once
  if (resubmissionAllowed[assignment.id]) {
    return 'submitted'; // This is a draft for resubmission, but show as submitted
  }
  // Otherwise it's pending/draft
  return 'pending';
};

export const computeStatusType = (assignment) => {
  // Weekly week-specific completion must be based on submitted_week_numbers.
  if (assignment.allow_weekly_submissions && assignment.week_number) {
    let submittedWeeks = assignment.submitted_week_numbers || [];
    if (typeof submittedWeeks === 'string') {
      try {
        submittedWeeks = JSON.parse(submittedWeeks);
      } catch (e) {
        submittedWeeks = [];
      }
    }

    const isSubmittedForThisWeek = Array.isArray(submittedWeeks)
      ? submittedWeeks.some(w => Number(w) === Number(assignment.week_number))
      : false;

    if (isSubmittedForThisWeek) {
      return 'completed';
    }

    if (assignment.is_extra_credit) {
      return 'upcoming';
    }
  }

  if (assignment.submitted_at || (assignment.grade !== null && assignment.grade !== undefined)) {
    return 'completed';
  }

  // For weekly assignments, use display_due_date (the calculated due date for the current week)
  // For regular assignments, use due_date
  let dueDate;
  if (assignment.allow_weekly_submissions && assignment.display_due_date) {
    // For weekly assignments with a calculated display_due_date, convert from Luxon DateTime
    try {
      if (typeof assignment.display_due_date === 'string') {
        dueDate = new Date(assignment.display_due_date);
      } else if (assignment.display_due_date && typeof assignment.display_due_date.toISO === 'function') {
        dueDate = new Date(assignment.display_due_date.toISO());
      } else {
        dueDate = null;
      }
    } catch (e) {
      dueDate = null;
    }
  } else {
    dueDate = assignment.due_date ? new Date(assignment.due_date) : null;
  }

  if (!dueDate || isNaN(dueDate.getTime())) {
    return 'upcoming';
  }

  const now = new Date();
  if (now > dueDate) {
    return 'overdue';
  }

  const diffDays = (dueDate - now) / (1000 * 60 * 60 * 24);
  if (diffDays <= 3) {
    return 'dueSoon';
  }

  return 'upcoming';
};

export const computeDisplayStatus = (assignment, resubmissionAllowed) => {
  const status = getStatus(assignment, resubmissionAllowed);
  if (status === 'late') {
    return { key: 'late', label: 'Late', icon: <FiAlertTriangle size={14} /> };
  }

  // Do not show an "Overdue" badge in My Assignments cards.
  return null;
};

export const getAllTabStatus = (assignment) => {
  if (assignment.statusType === 'completed') {
    return { key: 'completed', label: 'Completed' };
  }

  if (assignment.statusType === 'overdue') {
    return { key: 'overdue', label: 'Overdue' };
  }

  return { key: 'upcoming', label: 'Open' };
};

export const getAssignmentDueDate = (assignment) => {
  if (assignment?.is_extra_credit && !assignment?.display_due_date) {
    return null;
  }

  if (assignment.allow_weekly_submissions && assignment.display_due_date) {
    if (typeof assignment.display_due_date === 'string') {
      return new Date(assignment.display_due_date);
    }

    if (assignment.display_due_date && typeof assignment.display_due_date.toISO === 'function') {
      return new Date(assignment.display_due_date.toISO());
    }
  }

  return assignment.due_date ? new Date(assignment.due_date) : null;
};

export const isLateSubmission = (assignment, status) => {
  if (!assignment.submitted_at) return false;

  const dueDate = getAssignmentDueDate(assignment);
  if (!dueDate || isNaN(dueDate.getTime())) return false;

  const submittedAt = new Date(assignment.submitted_at);
  if (isNaN(submittedAt.getTime())) return false;

  return submittedAt.getTime() > dueDate.getTime();
};

export const getAssignmentUiKey = (assignment) => {
  return assignment?.allow_weekly_submissions && assignment?.week_number
    ? `${assignment.id}-week-${assignment.week_number}`
    : `${assignment?.id}`;
};

export const getPrimaryActionLabel = (assignment, status, resubmissionAllowed) => {
  if (['submitted', 'late', 'graded'].includes(status)) {
    return resubmissionAllowed[assignment.id] ? 'Revise Assignment' : 'View Submission';
  }

  if (status === 'draft') {
    return 'Resume Assignment';
  }

  return 'Start Assignment';
};

export const formatAssignmentDueDate = (assignment) => {
  if (assignment?.is_extra_credit && !assignment?.display_due_date && !assignment?.due_date) {
    return 'No due date';
  }

  if (assignment?.allow_weekly_submissions && assignment?.week_number && assignment?.display_due_date) {
    return formatUTCToChicago(assignment.display_due_date.toUTC().toISO(), 'MMM d \'·\' h:mm a');
  }

  if (!assignment?.due_date) {
    return 'No due date';
  }

  return formatUTCToChicago(assignment.due_date, 'MMM d \'·\' h:mm a');
};
